import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { IMAGES, type ImageId } from '../../assets/marketing/manifest';
import { focal } from '../content/images';
import type { VideoId } from '../content/videos';
import useReducedMotion from '../hooks/useReducedMotion';
import Picture from '../primitives/Picture';
import { useHeroContext } from './HeroContext';
import HeroVideo, { type HeroVideoState } from './HeroVideo';
import { useNavMode } from './NavModeContext';

const NAV_HEIGHT_PX = 80; // --mk-nav-h: 5rem at the 16px root
/** How much scrolling completes the landing → docked transition, as a fraction of the viewport. */
const TRAVEL = 0.5;
/** Matches --mk-dur; the outgoing layer is dropped once its fade has finished. */
const FADE_MS = 520;
export const HERO_PROGRESS_VAR = '--hero-p';

/** The photographs the other hero pages open on, warmed on idle so a dissolve never waits on the network. */
const HERO_IMAGES: ImageId[] = ['hero-frost-fields', 'hero-about', 'hero-laptop-sofa'];

const clamp01 = (n: number) => Math.min(1, Math.max(0, n));

interface Layer {
  image: ImageId;
  alt: string;
  video?: VideoId;
}

/**
 * The persistent hero: one full-bleed photograph with the nav laid over it
 * and no headline. Lives in the layout, so across the pages that share the
 * layout a route change dissolves one photograph into the next instead of
 * cutting.
 *
 * Two things are driven from scroll position and nothing else, so they are
 * smooth in both directions and settle wherever the page is reloaded:
 *
 *   --hero-p  0 → 1 over the first half viewport of scrolling, set on <html>.
 *             The nav's vertical position and the wordmark's size are CSS
 *             calc() of it (marketing.css): the page opens with the wordmark,
 *             links and button centred on the picture and they settle into
 *             the docked nav as the visitor scrolls. During a route change the
 *             nav eases the jump instead (NavMode.transitioning).
 *   onPhoto   whether the nav band is still over the photograph, which
 *             decides white-on-transparent versus black-on-Bone.
 *
 * Under reduced motion the progress is pinned at 1 — the docked layout — and
 * layers swap without a fade. The active image is preloaded so it is the LCP
 * candidate from the first byte of HTML the app renders.
 *
 * A layer may carry a film (HeroVideo) over its photograph, which is then the
 * film's still. The stage owns the Pause/Play control (WCAG 2.2.2) and shows
 * it only while the active film has played (a visitor who paused sees Play over the still when they come back).
 */
export default function HeroStage() {
  const ctx = useHeroContext();
  const requested = ctx?.hero ?? { image: null, alt: '' };
  const { setOnPhoto, setLanding, transitioning } = useNavMode();
  const reduced = useReducedMotion();
  const ref = useRef<HTMLElement | null>(null);
  const [layers, setLayers] = useState<Layer[]>([]);

  // The active film's state (each active HeroVideo reports it on mount and whenever it
  // becomes active) and the visitor's Pause, which lasts as long as the stage: the visit.
  const [videoState, setVideoState] = useState<HeroVideoState>('off');
  const [userPaused, setUserPaused] = useState(false);
  const activeVideo = layers[layers.length - 1]?.video;

  // A new image goes on top of the current one (at most two layers).
  useLayoutEffect(() => {
    if (!requested.image) return;
    const image = requested.image;
    const alt = requested.alt;
    const video = requested.video;
    setLayers((prev) => {
      const top = prev[prev.length - 1];
      if (top && top.image === image && top.video === video) return prev;
      return [...prev.slice(-1), { image, alt, video }];
    });
  }, [requested.image, requested.alt, requested.video]);

  // Drop the outgoing layer once the dissolve has finished.
  useEffect(() => {
    if (layers.length < 2) return undefined;
    const timer = setTimeout(() => setLayers((prev) => prev.slice(-1)), reduced ? 0 : FADE_MS);
    return () => clearTimeout(timer);
  }, [layers, reduced]);

  // A page with no hero: fade out, then unmount, unless another hero arrives first.
  useEffect(() => {
    if (requested.image || layers.length === 0) return undefined;
    const timer = setTimeout(() => setLayers([]), reduced ? 0 : FADE_MS);
    return () => clearTimeout(timer);
  }, [requested.image, layers.length, reduced]);

  const mounted = layers.length > 0;

  // Scroll-linked progress and the on-photo flag, while a hero is showing.
  useLayoutEffect(() => {
    const node = ref.current;
    if (!mounted || !node || typeof window === 'undefined') return undefined;
    const root = document.documentElement;
    setLanding(true);

    let raf = 0;
    const measure = () => {
      raf = 0;
      const rect = node.getBoundingClientRect();
      setOnPhoto(rect.bottom > NAV_HEIGHT_PX);
      const travel = Math.max(1, window.innerHeight * TRAVEL);
      const progress = reduced ? 1 : clamp01(window.scrollY / travel);
      root.style.setProperty(HERO_PROGRESS_VAR, progress.toFixed(4));
    };
    const schedule = () => {
      if (raf) return;
      raf = typeof requestAnimationFrame === 'function' ? requestAnimationFrame(measure) : (measure(), 0);
    };
    measure();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      if (raf && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(raf);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      setOnPhoto(false);
      setLanding(false);
      root.style.removeProperty(HERO_PROGRESS_VAR);
    };
  }, [mounted, reduced, setOnPhoto, setLanding]);

  // Re-measure when the page changes underneath a persistent stage (scroll
  // reset by ScrollToTop happens in the same commit, before this runs).
  useLayoutEffect(() => {
    if (!mounted || typeof window === 'undefined') return;
    window.dispatchEvent(new Event('scroll'));
  }, [requested.image, mounted]);

  // Warm the other pages' photographs on idle.
  useEffect(() => {
    if (!mounted || typeof window === 'undefined' || typeof Image === 'undefined') return undefined;
    const run = () => {
      HERO_IMAGES.filter((id) => id !== requested.image).forEach((id) => {
        const source = IMAGES[id].webp[Math.min(1, IMAGES[id].webp.length - 1)];
        const img = new Image();
        img.src = source.src;
      });
    };
    const idle = (window as Window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number })
      .requestIdleCallback;
    if (idle) {
      idle(run, { timeout: 3000 });
      return undefined;
    }
    const timer = setTimeout(run, 1500);
    return () => clearTimeout(timer);
  }, [mounted, requested.image]);

  if (!mounted) return null;
  // Nobody gets a dead button; a returning visitor who paused sees Play over the still.
  const showControl = Boolean(activeVideo) && videoState !== 'unavailable' && (videoState !== 'off' || userPaused);
  const active = layers[layers.length - 1];
  const entry = IMAGES[active.image];
  const preload = entry.webp[Math.min(1, entry.webp.length - 1)];

  return (
    <section
      ref={ref}
      className="mk-hero mk-hero--landing"
      data-transitioning={transitioning ? 'true' : 'false'}
      aria-label={active.alt || undefined}
    >
      <Helmet>
        <link
          rel="preload"
          as="image"
          href={preload.src}
          imageSrcSet={entry.webp.map((s) => `${s.src} ${s.w}w`).join(', ')}
          imageSizes="100vw"
        />
      </Helmet>
      <div className="mk-hero__frame">
        {layers.map((layer, index) => {
          const isActive = index === layers.length - 1;
          return (
            <div key={`${layer.image}|${layer.video ?? ''}`} className="mk-hero__layer" data-active={isActive ? 'true' : 'false'}>
              <Picture image={IMAGES[layer.image]} alt={layer.alt} sizes="100vw" priority fill focal={focal(layer.image)} />
              {layer.video ? (
                <HeroVideo
                  video={layer.video}
                  focal={focal(layer.image)}
                  paused={userPaused || !isActive}
                  onState={isActive ? setVideoState : undefined}
                />
              ) : null}
            </div>
          );
        })}
        <div className="mk-hero__tint" aria-hidden="true" />
        <div className="mk-hero__scrim" aria-hidden="true" />
        {showControl ? <div className="mk-hero__scrim mk-hero__scrim--bottom" aria-hidden="true" /> : null}
      </div>
      {showControl ? (
        <button type="button" className="mk-hero__pause t-details" onClick={() => setUserPaused((p) => !p)}>
          {userPaused ? 'Play' : 'Pause'}
          <span className="mk-sr-only"> background film</span>
        </button>
      ) : null}
    </section>
  );
}
